import { useNotifications } from '@/lib/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

export interface EntityMutationsConfig<TFormData, TEntity> {
  entityName: string;
  basePath: string;
  queryKeys: string[];
  createFn?: (data: TFormData) => Promise<unknown>;
  updateFn: (data: TFormData & { id: number }) => Promise<unknown>;
  deleteFn: (id: number) => Promise<unknown>;
  getDeleteConfirmMessage: (entity: TEntity) => string;
  getId: (entity: TEntity) => number;
}

export interface EntityMutationsActions<TFormData, TEntity> {
  handleFormSubmit: (
    formData: TFormData,
    mode: 'view' | 'create' | 'edit',
    entity: TEntity | null,
  ) => Promise<void>;
  handleDelete: (entity: TEntity) => void;
}

export interface UseEntityMutationsReturn<TFormData, TEntity> {
  actions: EntityMutationsActions<TFormData, TEntity>;
  isFormLoading: boolean;
  isDeleting: boolean;
}

export const createEntityMutationHook =
  <TFormData, TEntity>(config: EntityMutationsConfig<TFormData, TEntity>) =>
  () =>
    useEntityMutations<TFormData, TEntity>(config);

export const useEntityMutations = <TFormData, TEntity>(
  config: EntityMutationsConfig<TFormData, TEntity>,
): UseEntityMutationsReturn<TFormData, TEntity> => {
  const {
    entityName,
    basePath,
    queryKeys,
    createFn,
    updateFn,
    deleteFn,
    getDeleteConfirmMessage,
    getId,
  } = config;

  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const invalidateQueries = () => {
    queryKeys.forEach(key => {
      queryClient.invalidateQueries({ queryKey: [key] });
    });
  };

  const createMutation = useMutation({
    mutationFn: createFn,
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Success',
        message: `${entityName} created successfully`,
      });
      invalidateQueries();
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message:
          error.message || `Failed to create ${entityName.toLowerCase()}`,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateFn,
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Success',
        message: `${entityName} updated successfully`,
      });
      invalidateQueries();
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message:
          error.message || `Failed to update ${entityName.toLowerCase()}`,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFn,
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Success',
        message: `${entityName} deleted successfully`,
      });
      invalidateQueries();
      navigate(basePath);
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message:
          error.message || `Failed to delete ${entityName.toLowerCase()}`,
      });
    },
  });

  const handleFormSubmit = async (
    formData: TFormData,
    mode: 'view' | 'create' | 'edit',
    entity: TEntity | null,
  ) => {
    try {
      if (mode === 'create' && createFn) {
        await createMutation.mutateAsync(formData);
      } else if (mode === 'edit' && entity) {
        await updateMutation.mutateAsync({
          ...formData,
          id: getId(entity),
        });
      }
      navigate(basePath);
    } catch {
      // Error handled in mutation
    }
  };

  const handleDelete = (entity: TEntity) => {
    if (window.confirm(getDeleteConfirmMessage(entity))) {
      deleteMutation.mutate(getId(entity));
    }
  };

  return {
    actions: { handleFormSubmit, handleDelete },
    isFormLoading: createMutation.isPending || updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
